import './style.modules.scss'

import { BrandIcon, BrandIconColor } from '@ampcore/adel-react'
import { BackgroundColor } from '@ampcore/ampcom-contracts'
import { EmailPayloadTemplate } from '@ampcore/client-side-services'
import { AnalyticsTagBuilder } from '@dai-team/analytics-library'
import React, { useMemo, useState } from 'react'
import xss, { getDefaultWhiteList, IFilterXSSOptions } from 'xss'

import { usePageHeading } from '../../hooks/usePageHeading'
import { usePageTitle } from '../../hooks/usePageTitle'
import { EmailAdvisorModal } from '../EmailAdvisorModal/EmailAdvisorModal'
import type { AdvisorQuestionProps } from './AdvisorQuestion.type'
import { useResolvedDynamicContent } from './useResolvedDynamicContent'

export const AdvisorQuestion: React.FC<AdvisorQuestionProps> = props => {
  const {
    icon,
    sizingClasses,
    analyticsName,
    psnAnalytics,
    isEmailFormEnabled,
    emailGreeting,
    disclosureText,
    overrideRecipient,
    testId
  } = props

  const { dynamicContent, advisorData, isLoading } =
    useResolvedDynamicContent(props)
  const pageHeading = usePageHeading()
  const pageTitle = usePageTitle()
  const atb = useMemo(
    () =>
      new AnalyticsTagBuilder()
        .setDefaultName('AdvisorQuestion')
        .setAnalyticsName(analyticsName)
        .setPsnAnalytics(psnAnalytics),
    [analyticsName, psnAnalytics]
  )
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalMessage, setModalMessage] = useState('')

  const classNames = useMemo(() => {
    const base = ['Advisor--color', 'u-padding24', 'u-sm-marginHoriz24']
    switch (dynamicContent?.bgColor) {
      case BackgroundColor.PURPLE_TINT:
      case 'Purple': // deprecated color value
        base.push('u-bgColorPurpleLight10')
        break
      case BackgroundColor.MINT_TINT:
      case 'Mint': // deprecated color value
        base.push('u-bgColorMintLight10')
        break
      case BackgroundColor.LIME_TINT:
      case 'Lime': // deprecated color value
        base.push('u-bgColorLimeLight20')
        break
      case BackgroundColor.BERRY_TINT:
        base.push('u-bgColorBerryLight10')
        break
      case BackgroundColor.ORANGE_TINT:
        base.push('u-bgColorOrangeLight20')
        break
      case BackgroundColor.WHITE:
        base.push('u-bgColorWhite')
        break
      case BackgroundColor.STONE:
      default:
        base.push('u-bgColorStone')
    }
    return base
  }, [dynamicContent?.bgColor])

  const closeModal = () => {
    setIsModalOpen(false)
  }

  const emailMessage = () => {
    if (!dynamicContent?.emailBody) {
      return ''
    }

    const placeholderLinkText = (pageHeading || pageTitle || '').trim()

    const isPlainText = !/<[a-z][\s\S]*>/i.test(dynamicContent.emailBody)
    let message = dynamicContent.emailBody
    if (isPlainText) {
      message = message.replace(/(\r\n|\n|\r)/g, '<br/>')
    }
    return message.replace(
      '%article%',
      `<a href="${window.location.href}" target="_blank" rel="noopener noreferrer">${placeholderLinkText}</a>`
    )
  }
  const handleOpenModal = () => {
    const emailMessageHtml = emailMessage()
    setModalMessage(emailMessageHtml)
    setIsModalOpen(true)
  }

  if (!dynamicContent) {
    return null
  }

  return (
    <section
      className="AdvisorQuestion u-sm-sizeConstrainedFlush u-sizeConstrained component-wrapper u-paddingVert48"
      data-analytics-container="AdvisorQuestion"
      data-testid={testId ?? null}
    >
      <div
        className={sizingClasses}
        data-testid={testId ? `${testId}-sizing-classes` : null}
      >
        <div
          className={classNames.join(' ')}
          data-testid={testId ? `${testId}-bg-color-section` : null}
        >
          <div className="AdvisorContent">
            <div className="u-flex u-flexAlignItemsStart u-sm-flexCol">
              {icon && (
                <BrandIcon
                  icon={icon}
                  color={dynamicContent.iconColor ?? BrandIconColor.DARK_MINT}
                  fill={true}
                  className="BrandIcon--sizeMedium u-marginRight24"
                  testId={testId ? `${testId}-icon` : undefined}
                />
              )}
              <h3
                className="u-marginTop4 u-textDefault"
                data-testid={testId ? `${testId}-headline` : undefined}
              >
                {dynamicContent?.headline}
              </h3>
            </div>
            {dynamicContent?.questions && (
              <ul className="Content u-paddingTop24 u-paddingLeft24 u-margin0">
                {dynamicContent?.questions.map(question => (
                  <li
                    className="u-paddingLeft8 u-marginBottom16"
                    key={question}
                    dangerouslySetInnerHTML={{ __html: xss(question) }}
                  ></li>
                ))}
              </ul>
            )}
            <div className="card u-marginVert24"></div>
            <div className="u-flex  u-flexJustifyBetween u-md-flexCol u-sm-flexCol ">
              {isLoading && (
                <div
                  className="u-paddingBottom24 u-marginHoriz24 u-posRelative"
                  data-testid={testId ? `${testId}-loading` : undefined}
                >
                  <div className="Loading u-posAbsoluteCenter u-textColorBrand">
                    <div className="Loading-indicator"></div>
                    <div className="Loading-indicator"></div>
                    <div className="Loading-indicator"></div>
                  </div>
                </div>
              )}
              {!isLoading && (
                <>
                  <div className="u-flexGrow u-md-paddingBottom24 u-sm-paddingBottom24 ">
                    {dynamicContent?.body && (
                      <div
                        data-testid={testId ? `${testId}-body` : undefined}
                        dangerouslySetInnerHTML={{
                          __html: xss(dynamicContent?.body, {
                            whiteList: {
                              ...getDefaultWhiteList(),
                              span: ['class']
                            }
                          } as IFilterXSSOptions)
                        }}
                      ></div>
                    )}
                  </div>
                  {isEmailFormEnabled && advisorData?.email ? (
                    <div className="u-flexExpandLeft u-marginLeft80 u-paddingLeft64 u-md-marginLeft0 u-sm-marginLeft0 u-md-paddingLeft0 u-sm-paddingLeft0">
                      <button
                        className="Button Button--primary u-textNoWrap"
                        onClick={handleOpenModal}
                        rel="noreferrer"
                        {...(() => {
                          const attributes =
                            atb.dataAnalyticAttribute('primary-button')
                          if (!analyticsName) {
                            attributes['data-analytics'] =
                              `advisorQuestion-button${dynamicContent?.button?.name}`
                          }
                          return attributes
                        })()}
                      >
                        {dynamicContent?.button?.name}
                      </button>
                      <EmailAdvisorModal
                        analyticsName={analyticsName}
                        psnAnalytics={psnAnalytics}
                        isModalOpen={isModalOpen}
                        onClose={closeModal}
                        advisorInfo={{
                          name: advisorData?.name ?? '',
                          url: advisorData?.urls?.site
                        }}
                        payload={{
                          subject: dynamicContent.emailSubject!,
                          greeting: emailGreeting!,
                          message: modalMessage,
                          recipient: advisorData?.email,
                          template: EmailPayloadTemplate.CLIENT_INQUIRY,
                          overrideRecipient: overrideRecipient
                        }}
                        disclosureText={disclosureText}
                      />
                    </div>
                  ) : (
                    dynamicContent?.button && (
                      <div className="u-flexExpandLeft u-marginLeft80 u-paddingLeft64 u-md-marginLeft0 u-sm-marginLeft0 u-md-paddingLeft0 u-sm-paddingLeft0">
                        <a
                          className="Button Button--primary u-textNoWrap"
                          href={xss(dynamicContent?.button?.url)}
                          target={dynamicContent?.button?.target}
                          {...(() => {
                            const attributes =
                              atb.dataAnalyticAttribute('primary-button')
                            if (!analyticsName) {
                              attributes['data-analytics'] =
                                `advisorQuestion-button${dynamicContent?.button?.name}`
                            }
                            return attributes
                          })()}
                          data-testid={
                            testId ? `${testId}-primary-button` : undefined
                          }
                        >
                          {dynamicContent?.button?.name}
                        </a>
                      </div>
                    )
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
